// シフト決定画面のリスト表示の描画。index.blade.php の [grid-area:schedule]（リスト）と同じマークアップを出す

import { buildUrl, classNames, esc } from './html'
import { renderLecturerSelect } from './render_lecturer_select'

const FIXED_COLUMN_COUNT = 10
/** サーバー側の既定の並び順（LecturerScheduleDecisionService の sortable(['started_at' => 'asc'])） */
const DEFAULT_SORT = { sort: 'started_at', direction: 'asc' }
const TH_BASE = 'py-2 pr-1 whitespace-nowrap'
const TH_SORTABLE = `${TH_BASE} cursor-pointer hover:bg-gray-200 transition-colors duration-150 pointer-events-auto`
const TD_BASE = 'py-2 pr-1 whitespace-nowrap'

/**
 * 並び替えリンク（クリックは index.js が横取りして API を呼ぶ）
 *
 * @param {string} column
 * @param {string} label
 * @param {{sort: string, direction: string}|null} sort 現在の並び順
 * @param {string} indexUrl
 * @param {object} periodQuery
 * @returns {string}
 */
const sortLink = (column, label, sort, indexUrl, periodQuery) => {
    // 並び替え未指定のときはサーバーの既定（started_at asc）が有効なので、@sortablelink と同じくその列を有効表示にする
    const current = sort ?? DEFAULT_SORT
    const active = current.sort === column
    // direction は URL 由来の値なので、asc / desc 以外は asc として扱う（属性に埋め込むため許可リストで限定）
    const currentDirection = current.direction === 'desc' ? 'desc' : 'asc'
    const direction = active && currentDirection === 'asc' ? 'desc' : 'asc'
    // @sortablelink と同じアイコン（fa fa-sort / fa-sort-asc / fa-sort-desc。アンカーの外に置く）
    const icon = active ? `fa fa-sort-${currentDirection}` : 'fa fa-sort'

    return `<a href="${esc(buildUrl(indexUrl, { ...periodQuery, sort: column, direction }))}">${esc(label)}</a> <i class="${icon}"></i>`
}

/**
 * 行の背景色（Blade と同じ規則）
 *
 * @param {object} lesson
 * @returns {string}
 */
const rowColorClasses = (lesson) => {
    const decided = !lesson.is_main_lecturer_undecided
    if (decided && lesson.is_lesson_require_certified_lecturer) {
        return 'bg-gray-100 text-red-500'
    }
    if (decided) {
        return 'bg-gray-100'
    }

    return lesson.is_lesson_require_certified_lecturer ? 'bg-blue-50' : 'bg-white'
}

/**
 * 表頭
 *
 * @param {object} data
 * @param {{sort: object|null, indexUrl: string}} context
 * @returns {string}
 */
const renderHead = (data, { sort, indexUrl, showsOrganization }) => {
    const periodQuery = data.period.query
    const availabilityHeads = data.display.shows_availability
        ? data.lecturers.map((lecturer) => `<th class="${TH_BASE} text-vertical-rl">${esc(lecturer.full_name)}</th>`).join('')
        : ''

    return `
    <thead class="grid items-center col-span-full [grid-template-columns:subgrid] sticky top-0 pointer-events-none" role="rowgroup">
        <tr class="grid items-center col-span-full [grid-template-columns:subgrid] py-2 bg-gray-100" role="row">
            ${showsOrganization ? '<th class="py-2 pl-2 pr-1 whitespace-nowrap">企業名</th>' : ''}
            <th class="py-2 ${showsOrganization ? '' : 'pl-2 '}pr-1 whitespace-nowrap cursor-pointer hover:bg-gray-200 pointer-events-auto js-filter-certified" title="クリックで認定講師の有無でフィルタリング">
                認定講師
                <span class="js-filter-certified-indicator ml-1 text-xs">🔍</span>
            </th>
            <th class="${TH_SORTABLE}">${sortLink('started_at', '日時', sort, indexUrl, periodQuery)}</th>
            <th class="${TH_SORTABLE}">${sortLink('userGroup.name', 'ユーザーグループ', sort, indexUrl, periodQuery)}</th>
            <th class="py-2 pr-4 whitespace-nowrap cursor-pointer hover:bg-gray-200 transition-colors duration-150">分野</th>
            <th class="${TH_SORTABLE}">${sortLink('number_of_times', '実施回数', sort, indexUrl, periodQuery)}</th>
            <th class="${TH_BASE}">講義内容</th>
            <th class="${TH_SORTABLE} js-filter-main" title="クリックでメイン講師の設定状況でフィルタリング">
                メイン講師
                <span class="js-filter-main-indicator ml-1 text-xs">🔍</span>
            </th>
            <th class="${TH_BASE}">サポート講師</th>
            <th class="${TH_BASE}">オブザーバー講師</th>
            ${availabilityHeads}
            <th class="${TH_BASE}"></th>
        </tr>
    </thead>`
}

/**
 * 一覧の1行
 *
 * @param {object} lesson
 * @param {object} data
 * @param {boolean} showsOrganization 企業名列を出すか（全社版）
 * @returns {string}
 */
const renderRow = (lesson, data, showsOrganization) => {
    const availabilityCells = data.display.shows_availability
        ? data.lecturers.map((lecturer) => `<td class="${TD_BASE}">${esc((lesson.availabilities && lesson.availabilities[lecturer.id]) ?? '-')}</td>`).join('')
        : ''
    const rowAttributes = classNames(
        lesson.is_main_lecturer_undecided && 'data-main-lecturer-undecided="1"',
        lesson.is_lesson_require_certified_lecturer && 'data-certified="1"',
    )

    return `
    <tr ${rowAttributes} class="grid items-center col-span-full [grid-template-columns:subgrid] border-t-2 ${rowColorClasses(lesson)}" role="row">
        ${showsOrganization ? `<td class="py-2 pl-2 pr-1 whitespace-nowrap">${esc(lesson.user_group.organization_name)}</td>` : ''}
        <td class="${TD_BASE}">${lesson.is_lesson_require_certified_lecturer ? '<label class="checkmark">✔︎</label>' : ''}</td>
        <td class="${TD_BASE}">${esc(data.period.type === 'day' ? lesson.time_label : lesson.datetime_label)}</td>
        <td class="${TD_BASE}">${esc(lesson.user_group.name)}</td>
        <td class="py-2 pr-4 whitespace-nowrap">${esc(lesson.user_group.field)}</td>
        <td class="${TD_BASE}">${esc(lesson.number_of_times)}</td>
        <td class="${TD_BASE}">${esc(lesson.lecture_content)}</td>
        <td class="${TD_BASE}">${renderLecturerSelect(lesson, 'lecturer_user_id', true)}</td>
        <td class="${TD_BASE}">${renderLecturerSelect(lesson, 'support_lecturer_user_id', false)}</td>
        <td class="${TD_BASE}">${renderLecturerSelect(lesson, 'observer_lecturer_user_id', false)}</td>
        ${availabilityCells}
    </tr>`
}

/**
 * 「入力を確認する」と「未確定のコースを表示」（メイン講師未決定の授業が無いときはボタンを出さず、メッセージを最初から表示する）
 *
 * @param {boolean} hasUndecided
 * @returns {string}
 */
const renderActions = (hasUndecided) => {
    const scrollButtons = hasUndecided
        ? `
        <div class="js-scroll-to-undecided flex items-center text-indigo-600 text-sm">
            <button class="js-scroll-to-undecided-prev inline-flex items-center py-2 px-2 bg-white border border-indigo-600 rounded-l material-symbols-outlined text-lg leading-none [text-box:trim-both_cap_alphabetic] hover:opacity-50" type="button" title="前のメイン講師未決定の行を表示します">arrow_left</button>
            <span class="p-3 bg-white border-y border-indigo-600 text-sm [text-box:trim-both_cap_alphabetic]">未確定のコースを表示</span>
            <button class="js-scroll-to-undecided-next inline-flex items-center py-2 px-2 bg-white border border-indigo-600 rounded-r material-symbols-outlined text-lg leading-none [text-box:trim-both_cap_alphabetic] hover:opacity-50" type="button" title="次のメイン講師未決定の行を表示します">arrow_right</button>
        </div>
        <span class="js-scroll-to-undecided-message ml-2 text-sm text-gray-500" hidden>メイン講師未決定の授業はありません。</span>`
        : '<span class="js-scroll-to-undecided-message ml-2 text-sm text-gray-500">メイン講師未決定の授業はありません。</span>'

    return `
    <div class="flex items-center gap-x-2">
        <input type="submit" name="send" value="入力を確認する" class="admin-indigo-button">
        ${scrollButtons}
    </div>`
}

/**
 * リスト表示全体（確認画面へ送るフォームを含む）
 *
 * @param {object} data API のレスポンス
 * @param {{indexUrl: string, confirmUrl: string, csrf: string, sort: object|null, showsOrganization: boolean}} context
 * @returns {string}
 */
export const renderList = (data, context) => {
    // 全社版は企業名列があるため固定列が1列多い
    const fixedColumnCount = context.showsOrganization ? FIXED_COLUMN_COUNT + 1 : FIXED_COLUMN_COUNT
    const columnCount = data.display.shows_availability ? data.lecturers.length + fixedColumnCount : fixedColumnCount
    const rows = data.online_lessons.map((lesson) => renderRow(lesson, data, context.showsOrganization)).join('')

    return `
    <div class="[grid-area:schedule]">
        <form class="contents" method="post" action="${esc(buildUrl(context.confirmUrl, data.period.query))}">
            <input type="hidden" name="_token" value="${esc(context.csrf)}">
            <ul class="mt-3 list-disc list-inside text-sm text-red-600"></ul>
            <div class="max-w-[100cqw] max-h-[var(--schedule-h)] overflow-x-scroll overflow-y-auto js-scroll-to-undecided-container">
                <table class="grid [grid-template-columns:var(--tracks,repeat(var(--cols,${fixedColumnCount}),auto))] text-xs text-center max-w-full [container-type:inline-size]" role="table" style="--cols:${columnCount}">
                    ${renderHead(data, context)}
                    <tbody class="grid items-center col-span-full [grid-template-columns:subgrid]" role="rowgroup">${rows}</tbody>
                </table>
            </div>
            ${renderActions(data.online_lessons.some((lesson) => lesson.is_main_lecturer_undecided))}
        </form>
    </div>`
}
